import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Play, ExternalLink, FileText } from 'lucide-react';

const Philanthropy = () => {
  const [playingVideo, setPlayingVideo] = useState(null);
  
  // Video refs
  const instituteVideoRef = useRef(null);
  const bulletproofVideoRef = useRef(null);
  const filmScreeningVideoRef = useRef(null);

  const handlePlayVideo = (videoId, videoRef) => {
    if (playingVideo === videoId) {
      videoRef.current?.pause();
      setPlayingVideo(null);
    } else {
      setPlayingVideo(videoId);
      videoRef.current?.play();
    }
  };

  // Assets
  const assets = {
    tatreezWorkshop: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/akumedwq_PHOTO-2026-02-09-14-35-36.jpg",
    instituteVideo: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/ayt2ctle_VIDEO-2026-02-09-14-37-05.mp4",
    bulletproofVideo: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/pw89x43l_7e3fa106-dd52-466d-a66d-420468c9c9f1.MP4",
    filmScreeningVideo: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/cb4j7v38_VIDEO-2026-02-09-14-23-20.mp4",
    architecturePDF: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/hw0z5aes_M547%20MAH%20240330.pdf",
    exhibitPhoto: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/a2e9d6a5_PHOTO-2026-02-09-14-35-30.jpg",
    checkPhoto: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/fk24bl0f_PHOTO-2026-02-09-14-32-40.jpg",
    volunteersPhoto: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/icc5npir_PHOTO-2026-02-09-14-36-36.jpg"
  };

  return (
    <div className="bg-black pt-24 lg:pt-32">
      {/* Hero Section */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
            Philanthropy
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white font-normal leading-[0.95] tracking-tight">
            Building Cultural Infrastructure
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mt-12">
            <p className="font-serif text-xl md:text-2xl text-white/80 italic leading-relaxed">
              Philanthropy, to me, is not charity — it is architecture.
            </p>
            <p className="font-sans text-base text-white/60 leading-[1.8] font-light">
              It is the long-term work of building systems that allow culture to exist, evolve, and be protected beyond a single moment or exhibition. My philanthropic focus centers on creating sustainable platforms for Arab cultural expression, education, and global visibility.
            </p>
          </div>
          <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8 max-w-3xl">
            This work lives through institutions, exhibitions, public programs, and community-led experiences designed to carry culture forward with dignity and rigor.
          </p>
        </div>
      </section>

      {/* The National Arab Institute */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
              Founding Initiative
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
              The National Arab Institute
            </h2>
          </div>

          {/* Video - Full Width Horizontal */}
          <div className="relative aspect-video bg-neutral-900 overflow-hidden mb-12">
            <video
              ref={instituteVideoRef}
              src={assets.instituteVideo}
              className="w-full h-full object-cover"
              playsInline
              onClick={() => handlePlayVideo('institute', instituteVideoRef)}
            />
            {playingVideo !== 'institute' && (
              <button
                onClick={() => handlePlayVideo('institute', instituteVideoRef)}
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
              >
                <div className="w-24 h-24 rounded-full border-2 border-white flex items-center justify-center group-hover:bg-white/10 transition-colors">
                  <Play size={40} className="text-white ml-1" fill="currentColor" />
                </div>
              </button>
            )}
          </div>
          <p className="font-sans text-xs text-white/40 mb-12">
            Click to play: The National Arab Institute Vision
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light">
                The National Arab Institute was founded to address a critical gap: Arab history and identity are globally present, yet structurally underrepresented.
              </p>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
                The Institute is envisioned as a living cultural institution — not a traditional museum — combining exhibitions, education, architecture, and public programming to tell Arab stories with depth, context, and ownership.
              </p>
              
              {/* Mission Points */}
              <div className="mt-10">
                <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/40 mb-6">
                  Its mission is to:
                </p>
                <div className="space-y-4">
                  {[
                    "Preserve Arab cultural memory",
                    "Support contemporary Arab creators",
                    "Build global understanding through lived cultural experience"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <span className="w-1.5 h-1.5 bg-white/40 rounded-full mt-2 flex-shrink-0" />
                      <p className="font-sans text-base text-white/70">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="font-serif text-xl text-white italic mt-10">
                This is not about nostalgia. It is about continuity.
              </p>
            </div>

            <div>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light">
                The National Arab Institute is now partnering with ALKNZ Ventures to fund and scale cultural initiatives that highlight Arab culture on the global stage.
              </p>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
                This partnership bridges cultural vision with strategic capital, ensuring long-term institutional sustainability for Arab-led projects — from exhibitions and architecture to education and public programming.
              </p>
              <p className="font-serif text-lg text-white italic mt-8">
                Culture should not rely on fragility. It should be funded with intention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bulletproof Dreams */}
      <section className="py-14 md:py-24 lg:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Video */}
            <div className="lg:order-2">
              <div className="relative aspect-video bg-neutral-200 overflow-hidden">
                <video
                  ref={bulletproofVideoRef}
                  src={assets.bulletproofVideo}
                  className="w-full h-full object-cover"
                  playsInline
                  onClick={() => handlePlayVideo('bulletproof', bulletproofVideoRef)}
                />
                {playingVideo !== 'bulletproof' && (
                  <button
                    onClick={() => handlePlayVideo('bulletproof', bulletproofVideoRef)}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                  >
                    <div className="w-20 h-20 rounded-full border-2 border-white flex items-center justify-center group-hover:bg-white/10 transition-colors">
                      <Play size={32} className="text-white ml-1" fill="currentColor" />
                    </div>
                  </button>
                )}
              </div>
              <p className="font-sans text-xs text-black/40 mt-3">
                Click to play: Bulletproof Dreams Exhibition Walkthrough
              </p>
            </div>

            {/* Content */}
            <div className="lg:order-1">
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
                A Cultural Prototype
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-black font-normal tracking-tight">
                Bulletproof Dreams
              </h2>
              <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-8">
                One of the Institute's first major prototypes is Bulletproof Dreams Exhibit.
              </p>
              <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-6">
                We worked on bringing Bulletproof Dreams to the United States as a proof of concept — demonstrating how Arab stories can travel across borders while retaining emotional, political, and artistic integrity.
              </p>
              <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-6">
                The exhibition explores themes of memory, resilience, displacement, and imagination — using visual language that resonates beyond geography.
              </p>
              <p className="font-serif text-lg text-black italic mt-8">
                This project represents the blueprint for future Institute-supported exhibitions: globally relevant, locally rooted, and emotionally honest.
              </p>

              {/* Links */}
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <a
                  href="https://www.kursungecirmezdusler.com/en/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all"
                >
                  Explore Original Exhibition
                  <ExternalLink size={14} />
                </a>
                <a
                  href="https://www.youtube.com/watch?v=HzQmG73Ku1I"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-black/30 text-black font-sans text-[11px] tracking-[0.15em] uppercase hover:bg-black hover:text-white transition-all"
                >
                  Watch on YouTube
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture & Spatial Vision */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Design Vision
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
                Architecture & Spatial Vision
              </h2>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8">
                The architectural vision for the National Arab Institute is led by Fouad Samara, whose work reimagines Arab architectural heritage through a contemporary lens.
              </p>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
                The building is designed not just as a container for culture, but as a cultural statement in itself — merging spatial storytelling, material memory, and modern form.
              </p>
              
              <p className="font-serif text-xl text-white italic mt-10">
                Architecture here is narrative. Space becomes language.
              </p>

              {/* Links */}
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <a
                  href={assets.architecturePDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-4 border border-white/20 text-white hover:bg-white hover:text-black transition-all group"
                >
                  <FileText size={20} />
                  <div>
                    <p className="font-sans text-[11px] tracking-[0.15em] uppercase">
                      View Architecture Plans
                    </p>
                  </div>
                </a>
                <a
                  href="https://fouadsamaraarchitects.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-4 border border-white/20 text-white hover:bg-white hover:text-black transition-all"
                >
                  <span className="font-sans text-[11px] tracking-[0.15em] uppercase">
                    Fouad Samara Architects
                  </span>
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>

            {/* Exhibit Photo */}
            <div>
              <img
                src={assets.exhibitPhoto}
                alt="National Arab Institute Exhibition"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partnership with ALKNZ Ventures */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Column - Main Content */}
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Strategic Partnership · Philanthropy
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
                Managing Philanthropy at ALKNZ Ventures
              </h2>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-8">
                Mariam Sallam is a Partner at ALKNZ Ventures, where we invest not only in companies — but in culture, people, and long-term impact.
              </p>
              <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
                Over the next five years, we are committing <span className="text-white font-medium">$5–10 million USD</span> toward philanthropic initiatives across the West and the MENA region.
              </p>
              
              <a
                href="mailto:mariam@alknzventures.com"
                className="inline-flex items-center justify-center mt-10 px-12 py-5 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all duration-300"
              >
                mariam@alknzventures.com
              </a>
            </div>

            {/* Right Column - Focus Areas */}
            <div>
              <h3 className="font-serif text-2xl text-white mb-8">Our Focus</h3>
              
              <div className="space-y-6">
                <div className="border-t border-white/10 pt-6">
                  <h4 className="font-serif text-lg text-white mb-2">Cultural Projects</h4>
                  <p className="font-sans text-sm text-white/50 leading-relaxed font-light">
                    Museums, exhibitions, film, Tatreez workshops, storytelling, archives, and initiatives that preserve and elevate Arab identity globally.
                  </p>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h4 className="font-serif text-lg text-white mb-2">Education & Individual Advancement</h4>
                  <p className="font-sans text-sm text-white/50 leading-relaxed font-light">
                    Scholarships, student relocation support, technical education, and career development pathways.
                  </p>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h4 className="font-serif text-lg text-white mb-2">Nonprofits & Charities</h4>
                  <p className="font-sans text-sm text-white/50 leading-relaxed font-light">
                    Registered organizations focused on culture, community development, youth empowerment, and social services.
                  </p>
                </div>
              </div>

              <p className="font-sans text-sm text-white/40 italic mt-8">
                If you are building meaningful impact in these areas, we'd love to hear from you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Public Programs & Cultural Events */}
      <section className="py-14 md:py-24 lg:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
              Community Engagement
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-black font-normal tracking-tight">
              Public Programs & Cultural Events
            </h2>
            <p className="font-sans text-base text-black/60 leading-[1.8] font-light mt-6 max-w-2xl">
              Beyond exhibitions, the Institute curates public programs designed to bring Arab culture into shared, participatory spaces.
            </p>
          </div>

          {/* Programs Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
            {/* Film Screenings */}
            <div>
              <div className="relative aspect-video bg-neutral-200 overflow-hidden mb-6">
                <video
                  ref={filmScreeningVideoRef}
                  src={assets.filmScreeningVideo}
                  className="w-full h-full object-cover"
                  playsInline
                  onClick={() => handlePlayVideo('film', filmScreeningVideoRef)}
                />
                {playingVideo !== 'film' && (
                  <button
                    onClick={() => handlePlayVideo('film', filmScreeningVideoRef)}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                  >
                    <div className="w-16 h-16 rounded-full border-2 border-white flex items-center justify-center group-hover:bg-white/10 transition-colors">
                      <Play size={24} className="text-white ml-1" fill="currentColor" />
                    </div>
                  </button>
                )}
              </div>
              <h3 className="font-serif text-2xl text-black tracking-tight">
                Film Screenings
              </h3>
              <p className="font-sans text-sm text-black/60 leading-relaxed mt-4 font-light">
                Curated movie screenings highlighting Arab cinema — past and present — followed by moderated discussions exploring identity, history, and representation.
              </p>
              <p className="font-sans text-sm text-black/50 italic mt-4">
                These screenings serve as entry points for broader cultural dialogue.
              </p>
            </div>

            {/* Tatreez Workshops */}
            <div>
              <div className="aspect-video bg-neutral-200 overflow-hidden mb-6">
                <img
                  src={assets.tatreezWorkshop}
                  alt="Tatreez Workshop"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-serif text-2xl text-black tracking-tight">
                Tatreez Workshops
              </h3>
              <p className="font-sans text-sm text-black/60 leading-relaxed mt-4 font-light">
                Hands-on Tatreez workshops led by artisans and cultural practitioners, preserving Palestinian embroidery traditions while educating participants on the historical and social stories woven into each stitch.
              </p>
              <p className="font-sans text-sm text-black/50 italic mt-4">
                Tatreez is treated not as craft alone, but as archive.
              </p>
            </div>

            {/* Community Fundraising */}
            <div>
              <div className="aspect-video bg-neutral-200 overflow-hidden mb-6">
                <img
                  src={assets.checkPhoto}
                  alt="Community Fundraising Event"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-serif text-2xl text-black tracking-tight">
                Community Fundraising
              </h3>
              <p className="font-sans text-sm text-black/60 leading-relaxed mt-4 font-light">
                Organizing fundraising events for nonprofits and cultural organizations that support Arab communities. These gatherings bring together donors, advocates, and community leaders to create real impact.
              </p>
              <p className="font-sans text-sm text-black/50 italic mt-4">
                Philanthropy as collective action.
              </p>
            </div>
          </div>

          {/* Additional Event Photo */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="aspect-video overflow-hidden">
              <img
                src={assets.volunteersPhoto}
                alt="Community Event Volunteers"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex items-center">
              <div>
                <h3 className="font-serif text-2xl text-black tracking-tight">
                  Community Cultural Gatherings
                </h3>
                <p className="font-sans text-base text-black/60 leading-relaxed mt-4 font-light">
                  Intimate and large-scale events — including talks, panels, and cultural salons — bringing together artists, historians, students, and the public to engage with Arab culture beyond headlines and stereotypes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Looking Forward */}
      <section className="py-14 md:py-24 lg:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
              Looking Forward
            </p>
            <p className="font-sans text-base text-white/60 leading-[1.8] font-light">
              The philanthropic work of the National Arab Institute is ongoing — evolving through new partnerships, exhibitions, and public programs.
            </p>
            <p className="font-sans text-base text-white/60 leading-[1.8] font-light mt-6">
              Each project is designed to answer one question:
            </p>
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-normal leading-[1.15] tracking-tight mt-10 italic">
              How do we ensure Arab culture is not only remembered — but built into the future?
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Philanthropy;
